import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Plus } from "lucide-react";
import { toast } from "react-hot-toast";

import {
  addMasterCategory,
  fetchCatalogCategoryMaster,
} from "./catalogueMasterCategoryApi";

export default function AddMasterCategoryModal({ open, onClose, api }) {
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [search, setSearch] = useState("");
  const [masterCategories, setMasterCategories] = useState([]); // [{id, categoryName, ...}] or strings

  const normalizedList = useMemo(() => {
    // Support multiple backend shapes:
    // 1) data.response => array of objects
    // 2) data.data => array
    // 3) direct array
    const list = Array.isArray(masterCategories) ? masterCategories : [];
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((it) => {
      const name =
        (it?.categoryName ?? it?.category_name ?? it?.name ?? it ?? "") + "";
      return name.toLowerCase().includes(q);
    });
  }, [masterCategories, search]);

  const pullMasterCategories = async () => {
    if (!api) return;
    setListLoading(true);
    try {
      const data = await fetchCatalogCategoryMaster(api);
      if (data?.responseCode === "00") {
        const list =
          data?.response ?? data?.data ?? data?.categories ?? data?.result ?? [];
        setMasterCategories(Array.isArray(list) ? list : []);
      } else {
        setMasterCategories([]);
        toast.error(data?.responseMessage || "Failed to load master categories");
      }
    } catch (e) {
      setMasterCategories([]);
      toast.error("Error loading master categories");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setCategoryName("");
    setSearch("");
    pullMasterCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAdd = async () => {
    const name = categoryName.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }

    setLoading(true);
    try {
      const data = await addMasterCategory(api, name);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Master category added");
        setCategoryName("");
        await pullMasterCategories();
      } else {
        toast.error(data?.responseMessage || "Failed to add master category");
      }
    } catch (e) {
      toast.error("Error adding master category");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-3">
      <div className="max-h-[82vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-4 shadow-xl">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <div className="text-base font-bold text-gray-900">Master Categories</div>
            <div className="text-xs text-gray-500">
              Add a master category and view the existing list.
            </div>
          </div>
          <button className="rounded-md p-1 hover:bg-gray-100" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="border-t" />

        {/* Add form */}
        <div className="mt-3 rounded-xl border bg-white p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs text-gray-500">Category Name</label>
              <input
                className="h-10 w-full rounded-lg border px-3 text-sm outline-none"
                placeholder="e.g. Pizza"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
              />
            </div>
            <div className="flex items-end">
              <button
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#01C37D] px-3 text-sm font-semibold text-white disabled:opacity-60"
                onClick={handleAdd}
                disabled={loading}
              >
                <Plus size={16} /> {loading ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>

        {/* Search + list */}
        <div className="mt-3 rounded-xl border bg-white p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-gray-100 text-gray-700">
                <Search size={16} />
              </div>
              <input
                className="h-9 w-[260px] rounded-lg border px-3 text-sm outline-none"
                placeholder="Search master categories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              className="inline-flex h-9 items-center gap-2 rounded-lg border bg-white px-3 text-sm hover:bg-gray-50"
              onClick={pullMasterCategories}
              disabled={listLoading}
              title="Refresh"
            >
              {listLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {listLoading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
          ) : normalizedList.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">No master categories found.</div>
          ) : (
            <div className="max-h-[42vh] overflow-auto rounded-xl border">
              <div className="grid grid-cols-12 gap-2 border-b bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">
                <div className="col-span-2">#</div>
                <div className="col-span-10">Category</div>
              </div>
              {normalizedList.map((it, idx) => {
                const name =
                  (it?.categoryName ?? it?.category_name ?? it?.name ?? it ?? "") + "";
                const id = it?.id ?? it?.cat_id ?? it?.pk_id ?? idx + 1;
                return (
                  <div
                    key={String(id) + "_" + idx}
                    className="grid grid-cols-12 gap-2 border-b px-3 py-2 text-sm last:border-none"
                  >
                    <div className="col-span-2 text-gray-500">{idx + 1}</div>
                    <div className="col-span-10 font-medium text-gray-900">{name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button className="rounded-lg border px-3 py-1.5 text-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
